import React, { useEffect, useState } from 'react';
import { TruckIcon, MapPinIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../../../../utils/formatters';
import { useSelector, useDispatch } from 'react-redux';
import { 
  selectIsAuthenticated 
} from '../../../../../redux/selectors/authSelectors';
import { getDistance } from 'geolib';
import { 
  selectUserLocation, 
  detectUserLocationWithPlaceName, 
  selectLocationError,
  selectIsDetectingCountry
} from '../../../../../redux/slices/locationSlice';
import { isInternationalShipping, formatDistance } from '../../../../../utils/GeoShippingEstimator';
import { setUseEstimator } from '../../../../../redux/slices/deliverySlice';

/**
 * ShippingOption component for showing shipping option in DeliveryMethodSelection
 */
const ShippingOption = ({
  deliveryOption,
  setDeliveryOption,
  selectedAddress,
  shippingRateLoading,
  isCalculatingRates,
  availableRates,
  selectedRate,
  handleShippingRateSelect,
  calculateRates,
  sellerAddress,
  productId,
  shippingState,
  currency
}) => {
  // Redux
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userLocation = useSelector(selectUserLocation);
  const locationError = useSelector(selectLocationError);
  const isDetectingLocation = useSelector(selectIsDetectingCountry);
  
  // Local state just for UI display
  const [estimatedCost, setEstimatedCost] = useState(5.99);
  const [estimatedDays, setEstimatedDays] = useState('2-4');
  
  // Error message from shipping state
  const errorMessage = shippingState?.error?.message || 'Unable to calculate shipping rates';
  const ratesAttempted = shippingState?.ratesAttempted || false;
  
  // Check for address
  const hasAddress = !!selectedAddress && selectedAddress?.id;
  
  // NEW: Check if we should use estimator based on shipping state
  const useEstimator = !isAuthenticated || shippingState?.useEstimator || !hasAddress || false;
  const estimatorReason = !hasAddress ? 'no_address' : shippingState?.estimatorReason || null;
  
  // Get location display name
  const locationName = userLocation?.displayName || userLocation?.city || 'your location';
  
  // Normalize selected rate ID
  const selectedRateId = selectedRate?.id || selectedRate?.object_id || 
    (selectedRate && `${selectedRate.provider}-${selectedRate.serviceName}-${selectedRate.amount}`);
    
  // Debug information
  const debugInfo = {
    isAuthenticated,
    hasAddress,
    addressId: selectedAddress?.id || 'none',
    addressCity: selectedAddress?.city || 'unknown',
    isLoading: shippingRateLoading || isCalculatingRates,
    ratesCount: availableRates?.length || 0,
    hasSelectedRate: !!selectedRate,
    selectedRateId: selectedRateId || 'none',
    hasUserLocation: !!userLocation?.coordinates,
    userLocationCoords: userLocation?.coordinates || null,
    locationDisplayName: locationName,
    isDetectingLocation,
    locationError,
    useEstimator,
    estimatorReason
  };
  
  
  // Auto-select the first rate for authenticated users when not using estimator
  useEffect(() => {
    if (isAuthenticated && !useEstimator && availableRates?.length > 0 && !selectedRate && !shippingRateLoading && !isCalculatingRates) {
      handleShippingRateSelect(availableRates[0]);
    }
  }, [availableRates, selectedRate, shippingRateLoading, isCalculatingRates, handleShippingRateSelect, isAuthenticated, useEstimator]);
  
  // Update shipping estimate when user location or seller address changes
  useEffect(() => {
    // Only calculate estimates when we need them (non-authenticated or useEstimator flag is true)
    if (!useEstimator && isAuthenticated) {
      return;
    }
    
    // Use selected address coordinates if available when logged in, otherwise use browser geolocation
    const useAddressCoordinates = isAuthenticated && hasAddress && 
      selectedAddress?.latitude && selectedAddress?.longitude;
    
    const userCoords = useAddressCoordinates ? 
      { latitude: selectedAddress.latitude, longitude: selectedAddress.longitude } : 
      userLocation?.coordinates;
    
    if (!userCoords) {
      
      return;
    }
    
    if (!sellerAddress?.latitude || !sellerAddress?.longitude) {
      return;
    }
    
    
    const sellerCoords = {
      latitude: sellerAddress.latitude,
      longitude: sellerAddress.longitude
    };
    
    // Calculate distance in meters
    try {
      const distanceInMeters = getDistance(userCoords, sellerCoords);
      const distanceInMiles = distanceInMeters / 1609.34;
      const distanceInKm = distanceInMeters / 1000;
      
      // Create address objects for country comparison
      const fromAddressObj = {
        ...sellerAddress,
        country: sellerAddress.country || sellerAddress.countryCode
      };
      
      const toAddressObj = useAddressCoordinates ? 
        {
          ...selectedAddress,
          country: selectedAddress.country || selectedAddress.countryCode
        } : 
        {
          city: userLocation?.city || 'Unknown',
          country: userLocation?.country || userLocation?.countryCode || 'Unknown',
          latitude: userCoords.latitude,
          longitude: userCoords.longitude
        };
      
      // Use the dedicated function for determining if shipping is international
      const isInternational = isInternationalShipping(fromAddressObj, toAddressObj);
      
   
      
      // Calculate estimated cost based on country and distance
      const cost = calculateEstimatedCost(distanceInMeters, isInternational);
      setEstimatedCost(cost);
      
      // Estimate delivery days based on distance
      let estimatedDays;
      if (isInternational) {
        estimatedDays = distanceInMeters > 8000000 ? '10-15' : 
                        distanceInMeters > 5000000 ? '7-10' : '5-7';
      } else {
        estimatedDays = distanceInMeters > 1000000 ? '4-6' : 
                        distanceInMeters > 500000 ? '2-4' : '1-2';
      }
      
      setEstimatedDays(estimatedDays);
    } catch (error) {
      console.error('❌ Error calculating distance:', error);
    }
  }, [userLocation, sellerAddress, isAuthenticated, useEstimator, selectedAddress, hasAddress]);
  
  // Calculate estimated shipping cost based on distance and international status
  const calculateEstimatedCost = (distanceInMeters, isInternational) => {
    // Base shipping rate
    let baseRate = 5.99;
    
    // International shipping has higher base rate
    if (isInternational) {
      baseRate = 19.99; // Higher base rate for international
    }
    
    // Distance in miles
    const distanceInMiles = distanceInMeters / 1609.34;
    
    // Simple calculation: base rate + additional per mile
    let cost = baseRate;
    
    if (isInternational) {
      // International: steeper increase per mile
      cost += distanceInMiles * 0.015;
    } else {
      // Domestic: only add cost after first 20 miles
      if (distanceInMiles > 20) {
        cost += (distanceInMiles - 20) * 0.10;
      }
    }
    
    console.log('💰 SHIPPING COST DETAILS', {
      baseRate,
      distanceInMiles: distanceInMiles.toFixed(2),
      additionalCost: isInternational 
        ? (distanceInMiles * 0.015).toFixed(2) 
        : (Math.max(0, distanceInMiles - 20) * 0.10).toFixed(2),
      totalBeforeCap: cost.toFixed(2),
      cap: isInternational ? 59.99 : 29.99
    });
    
    // Cap at reasonable maximum - higher for international
    return Math.min(cost, isInternational ? 59.99 : 29.99);
  };
  
  // Get location display name - prefer selected address city when available
  const displayLocation = (hasAddress && selectedAddress?.city) ? 
    selectedAddress.city : 
    locationName;
  
  // Handle option selection
  const handleOptionSelect = () => {
    // Call the parent component's handler (which now toggles selection)
    setDeliveryOption('shipping');
    
    // Only try to get location when shipping is initially selected
    if (deliveryOption !== 'shipping') {
      // For non-logged in users or when using estimator, try to get location for estimate
      if ((useEstimator || !isAuthenticated) && !userLocation?.coordinates && !isDetectingLocation) {
        dispatch(detectUserLocationWithPlaceName());
      }
      
      // For logged in users with address, calculate shipping rates
      if (isAuthenticated && hasAddress && productId && sellerAddress && 
        (!ratesAttempted || 
        ((!availableRates || availableRates.length === 0) && !shippingState.error))) {
        calculateRates(selectedAddress);
      }
    }
  };
  
  // Helper function to consistently check if a rate is selected
  const isRateSelected = (rate) => {
    if (!selectedRate) return false;
    
    // Special handling for Colissimo
    if (rate.carrier_name === 'colissimo' || selectedRate.carrier_name === 'colissimo') {
      return rate.object_id === selectedRate.object_id;
    }
    
    // More aggressive normalization for comparison
    const normalizeRateForComparison = (r) => ({
      id: r.id || r.object_id || `${r.provider || r.carrier}-${r.servicelevel?.name || r.serviceName}-${r.amount || r.price}`,
      provider: r.provider || r.carrier,
      serviceName: r.servicelevel?.name || r.serviceName,
      amount: String(r.amount || r.price || '0')
    });
    
    const normalizedSelected = normalizeRateForComparison(selectedRate);
    const normalizedRate = normalizeRateForComparison(rate);
    
    return normalizedSelected.id === normalizedRate.id || 
      (normalizedSelected.provider === normalizedRate.provider && 
       normalizedSelected.serviceName === normalizedRate.serviceName && 
       normalizedSelected.amount === normalizedRate.amount);
  };
  
  // Get information for display
  const getRateDisplay = (rate) => {
    // Special display handling for Colissimo to ensure French language is preserved
    if (rate.carrier_name === 'colissimo') {
      return {
        provider: 'Colissimo',
        serviceName: rate.servicelevel?.name || rate.serviceName || 'Standard',
        amount: rate.amount || rate.price || '0.00',
        days: rate.estimated_days || rate.days || '3-5'
      };
    }
    
    return {
      provider: rate.provider || rate.carrier_name || rate.carrier || 'Carrier',
      serviceName: rate.servicelevel?.name || rate.serviceName || 'Standard',
      amount: rate.amount || rate.price || '0.00',
      days: rate.estimated_days || rate.days || '3-5'
    };
  };
  
  // Check if we're ready to show rates
  const showRatesSection = deliveryOption === 'shipping' && !shippingRateLoading && 
    !isCalculatingRates && !useEstimator && availableRates?.length > 0;
  
  const selectedRateDisplay = selectedRate ? getRateDisplay(selectedRate) : null;
  
  // Load estimates automatically regardless of shipping selection
  useEffect(() => {
    // Automatically enable estimator mode
    if (!shippingState.useEstimator) {
      dispatch(setUseEstimator(true));
    }
    
    // Attempt to get user location if needed and not already in progress
    // Only try if there's no location error (like permission denied)
    if ((useEstimator || !isAuthenticated) && !userLocation?.coordinates && !isDetectingLocation && !locationError) {
      dispatch(detectUserLocationWithPlaceName());
    }
    
    // Create an estimated rate if we don't have a selected rate yet
    if (!selectedRate && !isCalculatingRates && !shippingRateLoading) {
      // First try to use user's location if available
      if (userLocation?.coordinates) {
        
        // Generate estimate based on user location
        const estimatedRate = {
          provider: 'Estimated',
          serviceName: 'Standard',
          amount: estimatedCost.toString(),
          days: estimatedDays,
          currency: 'USD',
          isEstimate: true
        };
        
        handleShippingRateSelect(estimatedRate);
      } else {
        
        // Fallback to standard estimate when no location data
        const fallbackRate = {
          provider: 'Estimated',
          serviceName: 'Standard',
          amount: '5.99',
          days: '2-5',
          currency: 'USD',
          isEstimate: true
        };
        
        handleShippingRateSelect(fallbackRate);
      }
    }
  }, [selectedRate, isCalculatingRates, shippingRateLoading, userLocation, 
      estimatedCost, estimatedDays, handleShippingRateSelect, dispatch, 
      shippingState.useEstimator, useEstimator, isAuthenticated, isDetectingLocation, locationError]);
  
  // For non-logged in users or when using estimator, show simplified UI with estimate
  if (!isAuthenticated || useEstimator) {
    return (
      <div 
        className={`flex items-center p-3 rounded-md cursor-pointer ${
          deliveryOption === 'shipping' ? 'bg-brand-teal bg-opacity-10 border border-brand-teal' : 'border border-gray-200'
        }`}
        onClick={handleOptionSelect}
      >
        <input
          type="radio"
          checked={deliveryOption === 'shipping'}
          onChange={handleOptionSelect}
          className="h-4 w-4 text-brand-teal focus:ring-brand-teal"
        />
        <TruckIcon className="h-5 w-5 ml-2 text-gray-500" />
        <div className="ml-2 flex-1">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-900">Shipping</span>
            <span className="text-sm font-medium text-gray-900">
              {isDetectingLocation && !hasAddress ? (
                <div className="flex items-center">
                  <ArrowPathIcon className="h-3 w-3 animate-spin mr-1 text-brand-teal" />
                  <span className="text-xs text-gray-500">Estimating...</span>
                </div>
              ) : (
                'From '+ formatCurrency(estimatedCost, currency)
              )}
            </span>
          </div>
          <div className="text-xs text-gray-500 flex justify-between items-center mt-1">
            <span>
              {(userLocation?.coordinates || (hasAddress && selectedAddress?.latitude)) ? 
                `Estimated delivery to ${displayLocation}` : 
                isDetectingLocation ? 'Getting location for estimate...' : 
                locationError ? 'Using standard shipping estimate' : 'Estimated shipping cost'}
            </span>
            {((userLocation?.coordinates || (hasAddress && selectedAddress?.latitude))) && (
              <span className="text-xs">{estimatedDays} days</span>
            )}
          </div>
          {locationError && !hasAddress && (
            <div className="text-xs text-red-500 mt-1 flex items-center">
              <span>
                {locationError.includes('denied') || locationError.includes('permission') ? 
                  'Location access denied' : locationError}
              </span>
              <button 
                className="ml-2 text-brand-teal underline whitespace-nowrap" 
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(detectUserLocationWithPlaceName());
                }}
              >
                Try again
              </button>
            </div>
          )}
          {!isAuthenticated && (
            <div className="text-xs text-gray-400 mt-1 italic">
              For accurate rates, <a href="/login" className="text-brand-teal hover:underline">log in</a>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // For logged in users not using estimator, show the standard shipping option UI
  return (
    <>
      <div 
        className={`flex items-center p-3 rounded-md cursor-pointer ${
          deliveryOption === 'shipping' ? 'bg-brand-teal bg-opacity-10 border border-brand-teal' : 'border border-gray-200'
        }`}
        onClick={handleOptionSelect}
      >
        <input
          type="radio"
          checked={deliveryOption === 'shipping'}
          onChange={handleOptionSelect}
          className="h-4 w-4 text-brand-teal focus:ring-brand-teal"
        />
        <TruckIcon className="h-5 w-5 ml-2 text-gray-500" />
        <div className="ml-2 flex-1">
          <span className="text-sm font-medium text-gray-900">Shipping</span>
          
          {/* When shipping rates are available */}
          {hasAddress && !shippingRateLoading && 
           !isCalculatingRates && availableRates?.length > 0 && selectedRateDisplay && (
            <div className="text-xs text-gray-500">
              {selectedRateDisplay.provider} {selectedRateDisplay.serviceName}: {formatCurrency(selectedRateDisplay.amount, currency)}
              {selectedRateDisplay.days && (
                <span className="block mt-1">
                  Est. delivery in {selectedRateDisplay.days} days to {selectedAddress.city}
                </span>
              )}
            </div>
          )}
          
          {/* When shipping rates are loading */}
          {hasAddress && (shippingRateLoading || isCalculatingRates) && (
            <div className="text-xs text-gray-500">
              Calculating rates for {selectedAddress.city}...
            </div>
          )}
          
          {/* When shipping rates are available but no rate is selected */}
          {hasAddress && !shippingRateLoading && 
           !isCalculatingRates && availableRates?.length > 0 && !selectedRate && (
            <div className="text-xs text-gray-500">
              Select a shipping rate
            </div>
          )}
          
          {/* When there's an error loading shipping rates */}
          {hasAddress && !shippingRateLoading && 
           !isCalculatingRates && shippingState.error && (
            <div className="text-xs text-red-600 flex items-center mt-1">
              {errorMessage}
              {ratesAttempted && (
                <button 
                  className="ml-2 text-brand-teal underline" 
                  onClick={(e) => {
                    e.stopPropagation();
                    calculateRates(selectedAddress);
                  }}
                >
                  Try again
                </button>
              )}
            </div>
          )}
          
          {/* When shipping rates failed to load but no specific error */}
          {hasAddress && !shippingRateLoading && 
           !isCalculatingRates && (!availableRates || availableRates.length === 0) && !shippingState.error && ratesAttempted && (
            <div className="text-xs text-gray-500">
              No shipping rates available
              <button 
                className="ml-2 text-brand-teal underline" 
                onClick={(e) => {
                  e.stopPropagation();
                  calculateRates(selectedAddress);
                }}
              >
                Try again
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Shipping Rate Options (only show when shipping is selected and rates are available) */}
      {deliveryOption === 'shipping' && showRatesSection && (
        <div className="ml-8 space-y-2 mt-2 border-l-2 border-gray-100 pl-4">
          {availableRates.map((rate) => {
            // Generate a stable ID for the rate
            const rateId = rate.id || rate.object_id || `${rate.provider || rate.carrier}-${rate.servicelevel?.name || rate.serviceName}-${rate.amount || rate.price}`;
            const rateDisplay = getRateDisplay(rate);
            
            // Use the consistent helper function to check selection
            const isSelected = isRateSelected(rate);
            
            return (
              <div 
                key={rateId}
                className={`flex items-center p-2 rounded-md cursor-pointer ${
                  isSelected ? 'bg-brand-teal bg-opacity-5 border border-brand-teal' : 'border border-gray-100'
                }`}
                onClick={() => {
                  handleShippingRateSelect(rate);
                }}
              >
                <input
                  type="radio"
                  name="shipping-rate"
                  checked={isSelected}
                  onChange={() => {
                    handleShippingRateSelect(rate);
                  }}
                  className="h-3 w-3 text-brand-teal focus:ring-brand-teal"
                />
                <div className="ml-2 flex-1 flex justify-between items-center">
                  <span className="text-xs font-medium text-gray-700">
                    {rateDisplay.provider} {rateDisplay.serviceName}
                  </span>
                  <span className="text-xs font-medium text-gray-900">
                    {formatCurrency(rateDisplay.amount, currency)}
                  </span>
                </div>
                <div className="text-xs text-gray-500 ml-2">
                  {rateDisplay.days ? `${rateDisplay.days} days` : ''}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default ShippingOption; 